'use client'

import { useState, useEffect, useCallback, useMemo, memo } from 'react'
import dynamic from 'next/dynamic'
import { Media } from '@/components/Media'
import { CMSLink } from '@/components/Link'
import { useRouter } from 'next/navigation'
import { ChevronDown, ChevronsRight, Menu, X } from 'lucide-react'

// Lazy load submenu for better performance
const SubMenu = dynamic(() => import('./sub-menu'), {
  ssr: false,
  loading: () => null,
})

export const Header3 = memo(
  ({ navItems, logo, rightCta }: { navItems: any; logo: any; rightCta?: any }) => {
    const { push } = useRouter()
    const [isScrolled, setIsScrolled] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

    // Optimized scroll handler with throttling for better performance
    const handleScroll = useCallback(() => {
      const scrollY = window.scrollY || window.pageYOffset
      const scrolled = scrollY > 100

      if (scrolled !== isScrolled) {
        setIsScrolled(scrolled)

        // Store scroll state in localStorage (debounced)
        try {
          localStorage.setItem(
            'headerScrollState',
            JSON.stringify({
              isScrolled: scrolled,
              scrollY: scrollY,
              timestamp: Date.now(),
            }),
          )
        } catch (error) {
          // Handle localStorage quota exceeded or disabled
          console.warn('Unable to save scroll state:', error)
        }
      }
    }, [isScrolled])

    // Memoized scroll event options for performance
    const scrollOptions = useMemo(() => ({ passive: true }), [])

    useEffect(() => {
      // Restore scroll state from localStorage on mount
      try {
        const storedState = localStorage.getItem('headerScrollState')
        if (storedState) {
          const parsed = JSON.parse(storedState)
          // Only restore if the stored state is recent (within last 5 minutes)
          const isRecent = Date.now() - parsed.timestamp < 5 * 60 * 1000
          if (isRecent && typeof parsed.isScrolled === 'boolean') {
            setIsScrolled(parsed.isScrolled)
          }
        }
      } catch (error) {
        console.warn('Error parsing stored scroll state:', error)
      }

      // Initial scroll position check
      handleScroll()

      // Throttled scroll listener for better performance
      let ticking = false
      const throttledScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleScroll()
            ticking = false
          })
          ticking = true
        }
      }

      window.addEventListener('scroll', throttledScroll, scrollOptions)

      // Mobile menu event handlers
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isMobileMenuOpen) {
          setIsMobileMenuOpen(false)
        }
      }

      const handleResize = () => {
        if (window.innerWidth >= 768 && isMobileMenuOpen) {
          setIsMobileMenuOpen(false)
        }
      }

      document.addEventListener('keydown', handleEscape, { passive: true })
      window.addEventListener('resize', handleResize, { passive: true })

      // Cleanup function
      return () => {
        window.removeEventListener('scroll', throttledScroll)
        document.removeEventListener('keydown', handleEscape)
        window.removeEventListener('resize', handleResize)
      }
    }, [handleScroll, scrollOptions, isMobileMenuOpen])

    // Memoized navigation styles for performance
    const navStyles = useMemo(
      () => ({
        container: `relative rounded-2xl shadow-lg transition-all duration-500 ease-in-out will-change-transform ${
          isScrolled ? 'shadow-xl' : 'shadow-md'
        }`,
        mobileMenu:
          'md:hidden rounded-b-2xl max-h-[calc(100vh-120px)] overflow-y-auto overflow-x-hidden',
      }),
      [isScrolled],
    )

    return (
      <>
        {/* Custom scrollbar styles for mobile menu */}
        <style jsx>{`
          #mobile-navigation::-webkit-scrollbar {
            width: 6px;
          }
          #mobile-navigation::-webkit-scrollbar-track {
            background: transparent;
          }
          #mobile-navigation::-webkit-scrollbar-thumb {
            background: hsl(var(--border));
            border-radius: 3px;
          }
          #mobile-navigation::-webkit-scrollbar-thumb:hover {
            background: hsl(var(--border) / 0.8);
          }
        `}</style>

        <header
          className={`fixed top-0 left-0 right-0 z-50 font-sans will-change-transform${!isScrolled ? ' mb-6' : ''}`}
          role="banner"
        >
          <div className="container mx-auto 2xl:px-0 py-2 mt-4">
            <nav
              className={navStyles.container}
              role="navigation"
              aria-label="Main navigation"
              style={{
                backgroundColor: 'rgba(51, 51, 51, 0.75)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <div className="flex items-center justify-between px-4 md:px-5 lg:px-6 py-3 md:py-4">
                {/* Logo Section - SEO Optimized */}
                {logo?.url && (
                  <div className="flex-shrink-0">
                    <CMSLink
                      url="/"
                      type="custom"
                      className="flex items-center justify-center transition-transform duration-300 hover:scale-105"
                      aria-label="Go to homepage"
                      rel="dofollow"
                    >
                      <Media
                        resource={logo}
                        src={logo?.url} // Fallback src in case resource is incomplete
                        alt={logo?.alt || 'Company Logo'}
                        imgClassName="h-12 md:h-14 lg:h-16 xl:h-20 w-auto cursor-pointer"
                        priority={true}
                        quality={100}
                        sizes="(max-width: 768px) 64px, (max-width: 1024px) 64px, 80px"
                        fetchPriority="high"
                        loading="eager"
                      />
                    </CMSLink>
                  </div>
                )}

                {/* Navigation Links - SEO & A11y Optimized */}
                <ul className="hidden md:flex items-center flex-1 justify-center" role="list">
                  {navItems?.map((item: any, index: any) => {
                    return (
                      <li
                        className="relative group"
                        key={`index-${index}-${item?.link?.label}`}
                        role="listitem"
                      >
                        <CMSLink
                          {...(item?.link?.reference
                            ? {
                                type: 'reference',
                                reference: item.link.reference,
                                label: item?.link?.label,
                              }
                            : {
                                type: 'custom',
                                url: item?.link?.url || '/',
                                label: item?.link?.label,
                              })}
                          className="flex items-center gap-1 px-2 md:px-3 py-2 text-sm md:text-base font-semibold text-primary-foreground rounded-md transition-all duration-300 hover:bg-accent hover:text-accent-foreground group-hover:bg-accent group-hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          aria-haspopup={!!item?.subMenuItems?.length}
                          aria-expanded={false}
                          rel="dofollow"
                        >
                          <span>{item?.link?.label}</span>
                          {!!item?.subMenuItems?.length && (
                            <ChevronDown
                              className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180"
                              aria-hidden="true"
                            />
                          )}
                        </CMSLink>

                        {!!item?.subMenuItems?.length && <SubMenu items={item?.subMenuItems} />}
                        {/* Removed schema.org meta position */}
                      </li>
                    )
                  })}
                </ul>

                {/* CTA Button - SEO Optimized */}
                <div className="flex items-center gap-4">
                  {rightCta && rightCta?.length > 0 && (
                    <div className="hidden lg:block">
                      {rightCta?.map((item: any, index: any) => (
                        <CMSLink
                          key={item?.id || index}
                          {...(item?.link?.reference
                            ? {
                                type: 'reference',
                                reference: item.link.reference,
                                label: item?.link?.label,
                              }
                            : {
                                type: 'custom',
                                url: item?.link?.url || '/',
                                label: item?.link?.label,
                              })}
                          className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold text-sm px-6 py-3 rounded-full transition-all duration-300 hover:bg-primary/90 hover:shadow-md hover:scale-105 focus:ring-2 focus:ring-ring focus:ring-offset-2 will-change-transform"
                          aria-label={`${item?.link?.label} - Primary call to action`}
                          rel="dofollow"
                        >
                          <span>{item?.link?.label}</span>
                          <ChevronsRight className="w-4 h-4" />
                        </CMSLink>
                      ))}
                    </div>
                  )}

                  {/* Mobile Menu Toggle - A11y Optimized */}
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="md:hidden p-2 rounded-md bg-accent text-foreground transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-navigation"
                    type="button"
                  >
                    {isMobileMenuOpen ? (
                      <X className="w-6 h-6" aria-hidden="true" />
                    ) : (
                      <Menu className="w-6 h-6" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              {/* Mobile Navigation Menu - A11y & Performance Optimized */}
              {isMobileMenuOpen && (
                <div
                  id="mobile-navigation"
                  className={navStyles.mobileMenu}
                  role="navigation"
                  aria-label="Mobile navigation"
                  style={{
                    maxHeight: 'calc(100vh - 120px)',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  <div className="px-6 py-4 space-y-3">
                    {navItems?.map((item: any, index: any) => (
                      <div key={`mobile-${index}-${item?.link?.label}`} className="space-y-2">
                        <div onClick={() => setIsMobileMenuOpen(false)}>
                          <CMSLink
                            {...(item?.link?.reference
                              ? {
                                  type: 'reference',
                                  reference: item.link.reference,
                                  label: item?.link?.label,
                                }
                              : {
                                  type: 'custom',
                                  url: item?.link?.url || '/',
                                  label: item?.link?.label,
                                })}
                            className="block py-2 px-3 text-base font-medium text-primary-foreground rounded-md transition-colors duration-200"
                            rel="dofollow"
                          >
                            {item?.link?.label}
                          </CMSLink>
                        </div>
                        {/* Mobile Submenu */}
                        {item?.subMenuItems?.length > 0 && (
                          <div className="mt-2 ml-2 rounded-md md:ml-0 md:mt-0">
                            {item?.subMenuItems?.map((subItem: any, subIndex: any) => (
                              <div
                                key={`mobile-sub-${subIndex}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                <CMSLink
                                  {...(subItem?.link?.reference
                                    ? {
                                        type: 'reference',
                                        reference: subItem.link.reference,
                                        label: subItem?.link?.label,
                                      }
                                    : {
                                        type: 'custom',
                                        url: subItem?.link?.url || '/',
                                        label: subItem?.link?.label,
                                      })}
                                  className="block py-3 px-4 text-sm text-primary-foreground hover:text-primary-foreground/80 transition-all duration-200"
                                  rel="dofollow"
                                >
                                  {subItem?.link?.label}
                                </CMSLink>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Mobile CTA */}
                    {rightCta && rightCta?.length > 0 && (
                      <div className="pt-4">
                        {rightCta?.map((item: any, index: any) => (
                          <div
                            key={`mobile-cta-${item?.id || index}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <CMSLink
                              {...(item?.link?.reference
                                ? {
                                    type: 'reference',
                                    reference: item.link.reference,
                                    label: item?.link?.label,
                                  }
                                : {
                                    type: 'custom',
                                    url: item?.link?.url || '/',
                                    label: item?.link?.label,
                                  })}
                              className="block w-full bg-primary text-primary-foreground font-semibold text-center py-3 rounded-full transition-all duration-300 hover:bg-primary/90"
                              rel="dofollow"
                            >
                              {item?.link?.label}
                            </CMSLink>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </nav>
          </div>
        </header>
      </>
    )
  },
)

Header3.displayName = 'Header3'
